import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LogsTable from './LogsTable';
import FullPageLoader from 'components/common/FullPageLoader';
import { fetchMainAccountAppointmentLogs } from 'slices/mainAccount/mainAccountAppointmentSlice';
import { fetchMainAccountAppointmentView } from 'slices/mainAccount/mainAccountAppointmentSlice';
import ViewTable from 'views/appointment/view/ViewTable';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import Api from 'services/api';
import { setIframeUrl } from 'slices/iframeSlice';
import toast from 'react-hot-toast';
import { Video, Trash2 } from 'lucide-react';
import CancelModal from 'components/appointment/CancelModal';
import RescheduleModal from 'components/appointment/RescheduleModal';
import { setAddAppointmentPopup, appointmentsSelector, closeAppointmentPopup } from 'slices/appointmentSlice';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { CloseButton } from 'reactstrap';

const AppointmentView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [item, setItem] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [logs, setLogs] = React.useState([]);
    const [cancelModal, setCancelModal] = useState(false);
    const [rescheduleModal, setRescheduleModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [inviteGuestPopup, setInviteGuestPopup] = useState({ open: false });

    const { popup: appointmentPopUpState } = useSelector(appointmentsSelector);

    const callViewApi = async () => {
        const callBack = (success, message, data) => {
            setLoading(false);
            if (success) {
                setItem(data);
            } else {
                console.log(message);
            }
        };
        dispatch(fetchMainAccountAppointmentView(id, callBack));
    };

    const fetchLogs = async () => {
        const callBack = (success, message, data) => {
            if (success) setLogs(data);
        };
        dispatch(fetchMainAccountAppointmentLogs(id, callBack));
    };

    React.useEffect(() => {
        setLoading(true);
        callViewApi();
        fetchLogs();
    }, [id]);

    // Reload view when the edit/copy popup closes after a successful save
    React.useEffect(() => {
        if (!appointmentPopUpState?.show) {
            callViewApi();
            fetchLogs();
        }
    }, [appointmentPopUpState]);

    // --- Action handler (cancel / reschedule) ---
    const handleAction = async (data, endpoint) => {
        setActionLoading(true);
        try {
            const response = await Api.clientSetActionAssign(id, data, endpoint);
            if (response?.status >= 200 && response?.status < 300) {
                toast.success(response?.data?.message || 'Done.', { className: 'successToast' });
                setCancelModal(false);
                setRescheduleModal(false);
                callViewApi();
                fetchLogs();
            } else {
                const msg = response?.data?.message || response?.data?.error || 'Something went wrong.';
                toast.error(msg, { className: 'errorToast' });
            }
        } catch (err) {
            toast.error('An error occurred.', { className: 'errorToast' });
        } finally {
            setActionLoading(false);
        }
    };

    // --- Mark as Completed ---
    const handleMarkCompleted = async () => {
        setActionLoading(true);
        try {
            const response = await Api.clientMarkCompleted(id);
            if (response?.status >= 200 && response?.status < 300) {
                toast.success(response?.data?.message || 'Appointment marked as completed.', { className: 'successToast' });
                callViewApi();
                fetchLogs();
            } else {
                toast.error(response?.data?.message || 'Could not mark as completed.', { className: 'errorToast' });
            }
        } catch {
            toast.error('An error occurred.', { className: 'errorToast' });
        } finally {
            setActionLoading(false);
        }
    };

    // --- Join Video Call ---
    const handleJoinVideoCall = async () => {
        try {
            const response = await Api.clientJoinVideoCall(id);
            if (response?.status === 200) {
                dispatch(setIframeUrl(response?.data?.data?.iframe_url));
                navigate(`/user/appointments/view/${id}/join-room-call`);
            } else {
                const errorMsg = response?.data?.message || response?.data?.error;
                toast.error(errorMsg || 'Failed to join video call.', { className: 'errorToast' });
            }
        } catch (err) {
            toast.error('Error joining video call.', { className: 'errorToast' });
        }
    };

    // --- Copy / Edit ---
    const handleCopy = () => {
        dispatch(setAddAppointmentPopup({ show: true, popupType: 'copy', item }));
    };
    const handleEdit = () => {
        dispatch(setAddAppointmentPopup({ show: true, popupType: 'edit', item }));
    };

    // --- Invite Guests Popup ---
    const InviteGuestPopup = ({ open, onClose }) => {
        const [submitting, setSubmitting] = useState(false);
        const {
            control,
            handleSubmit,
            formState: { errors },
        } = useForm({
            defaultValues: {
                emailArray: item?.appointment_details?.requester_email
                    ? item.appointment_details.requester_email
                        .split(',')
                        .map((email) => ({ email: email.trim() }))
                    : [{ email: '' }],
            },
        });
        const { fields, append, remove } = useFieldArray({ control, name: 'emailArray' });

        const onSubmit = async (data) => {
            const emails = data.emailArray.map((e) => e.email.trim()).filter(Boolean);
            try {
                setSubmitting(true);
                const response = await Api.clientInviteGuests(id, { requester_email: emails });
                toast.success(response?.data?.message || 'Guests invited.');
                setInviteGuestPopup({ open: false });
                callViewApi();
            } catch {
                toast.error('Failed to invite guests.', { className: 'errorToast' });
            } finally {
                setSubmitting(false);
            }
        };

        if (!open) return null;

        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                background: 'rgba(0,0,0,0.3)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box',
            }}>
                <form onSubmit={handleSubmit(onSubmit)} style={{
                    background: '#fff', borderRadius: 12, minWidth: 500, maxWidth: 900,
                    width: '40%', maxHeight: '100%', padding: 32,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.15)', position: 'relative', overflowY: 'auto',
                }}>
                    <button type="button" onClick={onClose} style={{
                        position: 'absolute', top: 16, right: 16,
                        background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
                    }}>
                        <CloseButton style={{ width: 10, height: 10 }} />
                    </button>
                    <h2 style={{ marginBottom: 24 }}>Invite Guests</h2>

                    {fields.map((field, index) => (
                        <div key={field.id} className="w-100 row-primary">
                            <Controller
                                name={`emailArray.${index}.email`}
                                control={control}
                                rules={{
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                                        message: 'Invalid email format',
                                    },
                                }}
                                defaultValue={field.email}
                                render={({ field }) => (
                                    <input className="input-in type_inp" {...field} type="email" placeholder="Email" style={{ flexGrow: 1, marginRight: 8 }} />
                                )}
                            />
                            <button type="button" onClick={() => remove(index)} style={{
                                background: 'red', color: 'white', border: 'none',
                                borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                            }}>
                                <Trash2 style={{ width: 16, height: 16 }} />
                            </button>
                        </div>
                    ))}

                    {errors.emailArray && errors.emailArray.map((error, idx) =>
                        error?.email ? (
                            <div key={idx} style={{ color: 'red', marginBottom: 10, fontSize: 12 }}>
                                {error.email.message}
                            </div>
                        ) : null
                    )}

                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
                        <button type="button" onClick={() => append({ email: '' })} style={{
                            padding: '10px 16px', borderRadius: 6, border: '1px solid #888', background: 'white', cursor: 'pointer',
                        }}>
                            Add Email
                        </button>
                        <SubmitButton buttonType="submit" className="button" btnText="Send Invite" disabled={submitting} loader={submitting} />
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className="content" style={{}}>
            {loading && <FullPageLoader />}
            <div className="card_table view_table_custom">

                <div className="row_main_head">
                    <h2 className="main_heading">View Appointment</h2>
                    <div className="text-transform-capitalize main_heading">
                        Status : {item?.status}
                    </div>
                </div>

                <div className="row_table_head">
                    <div className="result-foot">
                        <span>Appointment ID : {item?.appid}</span>
                    </div>
                </div>

                {/* Action buttons */}
                <div className="row-primary flex-wrap justify-content-between mt-3 mb-2">
                    <div className="row-primary flex-wrap">
                        <SubmitButton
                            buttonType="button"
                            className="lightPurple"
                            btnText="Copy"
                            handleSubmit={handleCopy}
                        />
                        {item?.status !== 'completed' && (
                            <SubmitButton
                                buttonType="button"
                                className="lightGrey"
                                btnText="Edit"
                                handleSubmit={handleEdit}
                            />
                        )}
                        {item?.status !== 'completed' && (
                            <SubmitButton
                                buttonType="button"
                                className="lightBlue"
                                btnText="Reschedule"
                                handleSubmit={() => setRescheduleModal(true)}
                            />
                        )}
                        {item?.status !== 'cancelled' && item?.status !== 'completed' && (
                            <SubmitButton
                                buttonType="button"
                                className="tomatoRed"
                                btnText="Cancel"
                                handleSubmit={() => setCancelModal(true)}
                            />
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {item?.type === 'VRI' && item?.status !== 'completed' && (
                            <SubmitButton
                                className="Cancle_button"
                                btnText="Invite Guests"
                                handleSubmit={() => setInviteGuestPopup({ open: true })}
                            />
                        )}
                        {item?.type === 'VRI' &&
                            item?.status !== 'completed' &&
                            item?.appointment_details?.actual_total_duration > 0 && (
                            <SubmitButton
                                buttonType="button"
                                className="green"
                                btnText="Mark as Completed"
                                loader={actionLoading}
                                handleSubmit={handleMarkCompleted}
                            />
                        )}
                        {item?.type === 'VRI' && item?.status !== 'completed' && (
                            <SubmitButton
                                className="green"
                                btnText="Join Video Call"
                                icon={<Video />}
                                handleSubmit={handleJoinVideoCall}
                            />
                        )}
                    </div>
                </div>

                <ViewTable item={item} />
            </div>

            <div className="card_table p-4">
                <div className="row_main_head mb-3">
                    <h2 className="main_heading">Appointment Logs</h2>
                </div>
                <LogsTable logs={logs} />
            </div>

            <CancelModal
                isOpen={cancelModal}
                onClose={() => setCancelModal(false)}
                onConfirm={handleAction}
                appointmentId={id}
                item={item}
                loader={actionLoading}
            />
            <RescheduleModal
                isOpen={rescheduleModal}
                onClose={() => setRescheduleModal(false)}
                onConfirm={handleAction}
                appointmentId={id}
                item={item}
                loader={actionLoading}
            />
            <InviteGuestPopup
                open={inviteGuestPopup.open}
                onClose={() => setInviteGuestPopup({ open: false })}
            />
        </div>
    );
};

export default AppointmentView;
